import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Form, Input, Button, Skeleton } from "antd";
import toast from "react-hot-toast";
import Edit from "../../../assets/Edit";
import {
  getSiteSettingsApi,
  UpdateSiteSettingsApi,
} from "../../../api/request/setting/setting";
import LoadableButton from "../../button/LoadableButton";

const SiteSettings = () => {
  const userDetails = useSelector((state) => state.user.userDetails);
  const [form] = Form.useForm();
  const [settingsData, setSettingsData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);

  const isAdmin = userDetails?.role === "admin";

  const fields = [
    { name: "phone", label: "Phone", type: "phone" },
    { name: "email", label: "Email", type: "email" },
    { name: "address", label: "Address", type: "text" },
    { name: "address_url", label: "Address URL", type: "url" },
    { name: "website_url", label: "Website URL", type: "url" },
    { name: "facebook_url", label: "Facebook URL", type: "url" },
    { name: "instagram_url", label: "Instagram URL", type: "url" },
    { name: "twitter_url", label: "Twitter URL", type: "url" },
    { name: "linkedin_url", label: "Linkedin URL", type: "url" },
    { name: "playstore_url", label: "Playstore URL", type: "url" },
    { name: "applestore_url", label: "Applestore URL", type: "url" },
  ];

  const validation = {
    phone: [
      { pattern: /^\+?[\d\s()-]+$/, message: "Enter a valid phone number" },
    ],
    email: [{ type: "email", message: "Enter a valid email" }],
    url: [{ type: "url", message: "Enter a valid URL" }],
  };

  const getSiteSettings = async () => {
    try {
      setIsLoading(true);
      const { data } = await getSiteSettingsApi();
      if (data?.status) {
        setSettingsData(data?.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getSiteSettings();
  }, []);

  const handleSave = async (values) => {
    setIsUpdateLoading(true);
    try {
      const { data } = await UpdateSiteSettingsApi(values);
      if (data?.status) {
        setIsEditing(false);
        toast.success(data?.message);
        setSettingsData({ ...settingsData, ...data?.data });
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsUpdateLoading(false);
    }
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex justify-between border-b pb-3">
        <p className="font-semibold text-lg">Site Settings</p>
        {isAdmin ? (
          <button onClick={() => setIsEditing(!isEditing)}>
            <Edit />
          </button>
        ) : null}
      </div>

      <div className="pt-4">
        {isEditing ? (
          <Form
            form={form}
            layout="vertical"
            initialValues={settingsData}
            onFinish={handleSave}
          >
            {fields.map((field) => (
              <Form.Item
                key={field.name}
                name={field.name}
                label={field.label}
                rules={validation[field.type] || []}
              >
                <Input placeholder={`Enter ${field.label}`} />
              </Form.Item>
            ))}

            <LoadableButton
              className="bg-primary text-white uppercase font-semibold py-1 px-4 rounded-md"
              type="submit"
              lable="Update"
              loadingLable="Updating..."
              isLoading={isUpdateLoading}
            />

            <Button className="ms-3" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          </Form>
        ) : (
          <div className="space-y-4">
            {isLoading
              ? fields.map((field) => (
                  <div key={field.name} className="flex gap-3">
                    <Skeleton.Input active size="small" />
                    <Skeleton.Input
                      active
                      size="small"
                      className="!w-[300px]"
                    />
                  </div>
                ))
              : fields.map((field) => {
                  const value = settingsData[field?.name];
                  return (
                    <div key={field.name} className="flex gap-3">
                      <span className="font-medium">{field.label}:</span>
                      <span
                        className={`${
                          field.type === "url" && value
                            ? "text-blue-500 underline"
                            : ""
                        }`}
                      >
                        {field.type === "url" && value ? (
                          <a
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {value}
                          </a>
                        ) : (
                          value || "--"
                        )}
                      </span>
                    </div>
                  );
                })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SiteSettings;
